import { useEffect, useState } from 'react';
import ErrorUtils from 'src/utils/ErrorUtils';
import StringUtils from 'src/utils/stringUtils';
import apiService from '../ApiCallService';
import {
    CommonBalancePool,
    CommonBalancePoolExecutionStatus,
    CommonBalancePoolGrain,
    CommonBalancePoolStatus,
    CommonBalancePoolValidationResult,
    CreateCommonBalancePoolRequest,
    DownloadCommonBalancePoolResponse,
    ExecuteCommonBalancePoolResponse,
    GetCommonBalancePoolPeriodResponse,
    GetCommonBalancePoolRowsResponse,
    ListCommonBalancePoolsResponse,
    PreviewCommonBalancePoolResponse,
    UpdateCommonBalancePoolRequest,
    UpdateCommonBalancePoolResponse,
} from 'src/models/multidimensional-allocation/CommonBalancePool';

/**
 * Service for the Common Balance Pool feature (MDA 1.1 Phase 2).
 *
 * Calls the real CBP WebAPI (`ICTransferPricingWebAPILambdaModel` contract, CR-297689232) through
 * `ApiCallService` — no mock data. Until the CBP WebAPI handlers + DAO + DynamoDB table are deployed
 * behind the `/api` proxy, `GetCommonBalancePools` returns 403/404 and the list renders its error
 * state; once the endpoint answers with `{ pools: [] }` the list renders empty, and with data it
 * renders the rows.
 */
export default class CommonBalancePoolService {
    /**
     * Lists Common Balance Pools, optionally filtered by grain / status. Backs the CBP tab and the
     * grain-filtered picker on the MDA add-data-source flow. Hook-tuple contract mirrors
     * `MultidimensionalAllocationService.listMultiAllocationSheets`: [result, loading, error].
     */
    listCommonBalancePools(
        query: string,
        grain?: CommonBalancePoolGrain,
        status?: CommonBalancePoolStatus,
    ): [ListCommonBalancePoolsResponse | null, boolean, string] {
        const [result, setResult] = useState(null as ListCommonBalancePoolsResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            // `active` guards against a slower earlier request resolving after a newer one (stale
            // overwrite) and against setState-after-unmount.
            let active = true;
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setError(''); // clear any prior error so a successful refetch doesn't show a stale banner
                    const response = await apiService.getCommonBalancePools(grain, status);
                    const json = (await response.json()) as ListCommonBalancePoolsResponse;
                    if (active) setResult({ pools: json.pools ?? [] });
                } catch (ex) {
                    if (active) {
                        // Keep the last-good result on error (matching MultidimensionalAllocationService
                        // .listMultiAllocationSheets): a failed refetch leaves the prior list on screen with
                        // the error banner over it, rather than blanking a list the user was working with.
                        // The banner renders independently of items, so the failure is always surfaced.
                        setError(ErrorUtils.getMessage(ex));
                    }
                } finally {
                    if (active) setLoading(false);
                }
            };

            if (!StringUtils.isNullOrEmpty(query)) {
                fetchData();
            }
            return () => {
                active = false;
            };
        }, [query, grain, status]);

        return [result, loading, error];
    }

    /**
     * Gets one pool definition, including its full column list. Defaults to the working definition (draft if
     * present, else live); pass `version` to read a specific one (`-1` draft, `0` live, `1..N` archived). The
     * backend accepts `-1`/`0`/`1..N`; note that reading an *earlier period's* formulas by archived version
     * depends on provenance that has not landed yet — see `getCommonBalancePoolPeriod`.
     */
    async getCommonBalancePool(poolId: string, version?: number): Promise<CommonBalancePool | null> {
        const response = await apiService.getCommonBalancePool(poolId, version);
        const json = (await response.json()) as { pool?: CommonBalancePool };
        return json.pool ?? null;
    }

    /**
     * Reports which definition version produced a period's numbers — the period→formula join backing
     * `GET /common-balance-pool/{poolId}/executions/{period}`.
     *
     * Two caveats the consumer (CR-2) must honour, both pinned by the backend
     * (`GetCommonBalancePoolPeriodComponent`):
     * - `definitionVersion` is currently the row **lane** (`0` = live), NOT the live version's own number:
     *   until the backend records `current_version` alongside the lane, every published period reports `0`,
     *   which `getCommonBalancePool(poolId, version)` does not accept as an archived version. Treat it as the
     *   lane, not as provenance, until that lands.
     * - An unmaterialised period is **refused with an error** (this call rejects), not returned as a 200 with
     *   `definitionVersion` absent — handle the rejection rather than a null field.
     */
    async getCommonBalancePoolPeriod(poolId: string, period: string): Promise<GetCommonBalancePoolPeriodResponse> {
        const response = await apiService.getCommonBalancePoolPeriod(poolId, period);
        return (await response.json()) as GetCommonBalancePoolPeriodResponse;
    }

    /** Creates a pool in DRAFT_IN_PROGRESS (Step 1: name + grain). */
    async createCommonBalancePool(request: CreateCommonBalancePoolRequest): Promise<CommonBalancePool> {
        const response = await apiService.createCommonBalancePool(request);
        const json = (await response.json()) as { pool: CommonBalancePool };
        return json.pool;
    }

    /**
     * Updates a draft pool (Steps 1-2) and/or drives a status transition. Returns the whole response —
     * `pool` plus `validationResult` — because a BLOCKING finding means the update was refused, so the
     * caller must branch on it rather than assume the save succeeded.
     */
    async updateCommonBalancePool(
        poolId: string,
        request: UpdateCommonBalancePoolRequest,
    ): Promise<UpdateCommonBalancePoolResponse> {
        const response = await apiService.updateCommonBalancePool(poolId, request);
        return (await response.json()) as UpdateCommonBalancePoolResponse;
    }

    /** Validates a pool without materialising it (the validation panel's on-demand check). */
    async validateCommonBalancePool(
        poolId: string,
        period?: string,
    ): Promise<CommonBalancePoolValidationResult> {
        const response = await apiService.validateCommonBalancePool(poolId, period);
        const json = (await response.json()) as { validationResult: CommonBalancePoolValidationResult };
        return json.validationResult;
    }

    /** Materialises a bounded sample so the builder can show a grid before a full run (Step 3). */
    async previewCommonBalancePool(
        poolId: string,
        period: string,
        rowLimit?: number,
    ): Promise<PreviewCommonBalancePoolResponse> {
        const response = await apiService.previewCommonBalancePool(poolId, period, rowLimit);
        return (await response.json()) as PreviewCommonBalancePoolResponse;
    }

    /**
     * Triggers a full materialisation for one period. The 202 returns only `{ executionId, state }`;
     * the run's detail (rowCount, timestamps, validation) is read by polling
     * getCommonBalancePoolExecutionStatus. Execute is idempotent on (poolId, period).
     */
    async executeCommonBalancePool(
        poolId: string,
        period: string,
        version: number,
    ): Promise<ExecuteCommonBalancePoolResponse> {
        const response = await apiService.executeCommonBalancePool(poolId, period, version);
        return (await response.json()) as ExecuteCommonBalancePoolResponse;
    }

    /** Polls the status of a materialisation run. */
    async getCommonBalancePoolExecutionStatus(
        executionId: string,
    ): Promise<CommonBalancePoolExecutionStatus> {
        const response = await apiService.getCommonBalancePoolExecutionStatus(executionId);
        const json = (await response.json()) as { executionStatus: CommonBalancePoolExecutionStatus };
        return json.executionStatus;
    }

    /** Reads materialised rows for a period, paginated (a pool can reach ~10k rows). */
    async getCommonBalancePoolRows(
        poolId: string,
        period: string,
        pageSize?: number,
        nextToken?: string,
    ): Promise<GetCommonBalancePoolRowsResponse> {
        const response = await apiService.getCommonBalancePoolRows(poolId, period, pageSize, nextToken);
        return (await response.json()) as GetCommonBalancePoolRowsResponse;
    }

    /** Builds a spreadsheet of the period's materialised rows and returns a presigned download URL. */
    async downloadCommonBalancePool(poolId: string, period: string): Promise<DownloadCommonBalancePoolResponse> {
        const response = await apiService.downloadCommonBalancePool(poolId, period);
        return (await response.json()) as DownloadCommonBalancePoolResponse;
    }
}
